<!--
~ Copyright (C) 2018 DV Bern AG, Switzerland
~
~ This program is free software: you can redistribute it and/or modify
~ it under the terms of the GNU Affero General Public License as
~ published by the Free Software Foundation, either version 3 of the
~ License, or (at your option) any later version.
~
~ This program is distributed in the hope that it will be useful,
~ but WITHOUT ANY WARRANTY; without even the implied warranty of
~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
~ GNU Affero General Public License for more details.
~
~ You should have received a copy of the GNU Affero General Public License
~ along with this program.  If not, see <https://www.gnu.org/licenses/>.
-->

@if (useInputComponents) {
    <div class="row">
        <!-- Rolle -->
        <div [class]="displayClass">
            <div class="form-group">
                <dv-benutzer-rolle
                    [inputId]="rolleId"
                    [name]="rolleId"
                    [required]="true"
                    [disabled]="disabled"
                    [excludedRoles]="excludedRoles"
                    [(benutzerRolle)]="berechtigung.role"
                    [needToShowRequired]="needToShowRequired"
                >
                </dv-benutzer-rolle>
            </div>
        </div>
        <!-- Gemeinde(n) -->
        @if (berechtigung.hasGemeindeRole()) {
            <div [class]="displayClass">
                <dv-gemeinde-multiselect
                    [selected]="berechtigung.gemeindeList"
                    [disabled]="disabled"
                    [required]="true"
                    [needToShowRequired]="needToShowRequired"
                >
                </dv-gemeinde-multiselect>
            </div>
        }
        <!-- Institution -->
        @if (berechtigung.hasInstitutionRole()) {
            <div [class]="displayClass">
                <div class="form-group">
                    <label class="required" translate [for]="institutionId"
                        >INSTITUTION</label
                    >

                    <mat-select
                        [attr.aria-describedby]="institutionId + '-error'"
                        [name]="institutionId"
                        [id]="institutionId"
                        class="form-control"
                        [disabled]="disabled"
                        [required]="true"
                        [ngModel]="berechtigung.institution"
                        [compareWith]="compareById"
                        [style.pointer-events]="disabled ? 'none' : 'auto'"
                    >
                        @for (
                            institution of institutionen$ | async;
                            track institution
                        ) {
                            <mat-option
                                (onSelectionChange)="
                                    onInstitutionSelectionChange($event)
                                "
                                [value]="institution"
                            >
                                {{ institution.name }}
                            </mat-option>
                        }
                    </mat-select>
                    <dv-error-messages
                        [inputId]="institutionId"
                        [errorObject]="form.controls[institutionId]?.errors"
                    >
                    </dv-error-messages>
                </div>
            </div>
        }
        <!-- Traegerschaft -->
        @if (berechtigung.hasTraegerschaftRole()) {
            <div [class]="displayClass">
                <div class="form-group">
                    <label class="required" translate [for]="traegerschaftId"
                        >TRAEGERSCHAFT</label
                    >
                    <mat-select
                        [attr.aria-describedby]="traegerschaftId + '-error'"
                        [name]="traegerschaftId"
                        [id]="traegerschaftId"
                        class="form-control"
                        [disabled]="disabled"
                        [required]="true"
                        [ngModel]="berechtigung.traegerschaft"
                        [compareWith]="compareById"
                        [style.pointer-events]="disabled ? 'none' : 'auto'"
                    >
                        @for (
                            traegerschaft of traegerschaften$ | async;
                            track traegerschaft
                        ) {
                            <mat-option
                                (onSelectionChange)="
                                    onTraegerschaftSelectionChange($event)
                                "
                                [value]="traegerschaft"
                            >
                                {{ traegerschaft.name }}
                            </mat-option>
                        }
                    </mat-select>
                    <dv-error-messages
                        [inputId]="traegerschaftId"
                        [errorObject]="form.controls[traegerschaftId]?.errors"
                    >
                    </dv-error-messages>
                </div>
            </div>
        }
        <!-- Sozialdienst -->
        @if (berechtigung.hasSozialdienstRole()) {
            <div [class]="displayClass">
                <div class="form-group">
                    <label class="required" translate [for]="sozialdienstId"
                        >SOZIALDIENST_TITLE</label
                    >
                    <mat-select
                        [attr.aria-describedby]="sozialdienstId + '-error'"
                        [name]="sozialdienstId"
                        [id]="sozialdienstId"
                        class="form-control"
                        [disabled]="disabled"
                        [required]="true"
                        [ngModel]="berechtigung.sozialdienst"
                        [compareWith]="compareById"
                        [style.pointer-events]="disabled ? 'none' : 'auto'"
                    >
                        @for (
                            sozialdienst of sozialdienste$ | async;
                            track sozialdienst
                        ) {
                            <mat-option
                                (onSelectionChange)="
                                    onSozialdienstSelectionChange($event)
                                "
                                [value]="sozialdienst"
                            >
                                {{ sozialdienst.name }}
                            </mat-option>
                        }
                    </mat-select>
                    <dv-error-messages
                        [inputId]="sozialdienstId"
                        [errorObject]="form.controls[sozialdienstId]?.errors"
                    >
                    </dv-error-messages>
                </div>
            </div>
        }
    </div>
}
@if (!useInputComponents) {
    <div>
        <fieldset>
            <table class="berechtigung">
                <thead></thead>
                <tbody>
                    <tr>
                        <td class="bold" translate>ROLLE</td>
                        <td translate>
                            {{ translationKeyForRole(berechtigung.role) }}
                        </td>
                    </tr>
                    @if (berechtigung.hasGemeindeRole()) {
                        <tr>
                            <td class="bold" translate>GEMEINDEN</td>
                            <td>{{ berechtigung.gemeindeListToString() }}</td>
                        </tr>
                    }
                    @if (berechtigung.hasInstitutionRole()) {
                        <tr>
                            <td class="bold" translate>INSTITUTION</td>
                            <td>{{ berechtigung.institution.name }}</td>
                        </tr>
                    }
                    @if (berechtigung.hasTraegerschaftRole()) {
                        <tr>
                            <td class="bold" translate>TRAEGERSCHAFT</td>
                            <td>{{ berechtigung.traegerschaft.name }}</td>
                        </tr>
                    }
                    @if (berechtigung.hasSozialdienstRole()) {
                        <tr>
                            <td class="bold" translate>SOZIALDIENST_TITLE</td>
                            <td>{{ berechtigung.sozialdienst.name }}</td>
                        </tr>
                    }
                </tbody>
            </table>
        </fieldset>
    </div>
}
